import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import { 
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Place as PlaceIcon,
  Receipt as ReceiptIcon,
  QrCode as QrCodeIcon,
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import BookingService, { PackageBooking } from '../services/bookingService';
import { useUser } from '../contexts/UserContext';
// import QRCode from 'react-qr-code';

const BookingDetailPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useUser();
  
  const [booking, setBooking] = useState<PackageBooking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingId || !currentUser) return;
      
      try {
        setLoading(true);
        const bookingData = await BookingService.getBookingById(bookingId);
        
        if (!bookingData) {
          setError('Booking not found');
          return;
        }
        
        // Security check - only allow users to view their own bookings
        if (bookingData.userId !== currentUser.uid) {
          setError('You do not have permission to view this booking');
          return;
        }
        
        setBooking(bookingData);
      } catch (err) {
        console.error('Error fetching booking:', err);
        setError('Failed to load booking details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBooking();
  }, [bookingId, currentUser]);
  
  const handleCancelBooking = async () => {
    if (!bookingId || !window.confirm('Are you sure you want to cancel this booking?')) return;
    
    try {
      await BookingService.cancelBooking(bookingId);
      // Refresh booking data
      const updated = await BookingService.getBookingById(bookingId);
      setBooking(updated);
    } catch (err) {
      console.error('Error cancelling booking:', err);
      alert('Failed to cancel booking. Please try again.');
    }
  };
  
  const handlePrint = () => {
    window.print();
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error || !booking) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Booking not found'}</Alert>
        <Button 
          onClick={() => navigate('/my-bookings')}
          sx={{ mt: 2 }}
        >
          Back to My Bookings
        </Button>
      </Container>
    );
  }
  
  const bookingDate = booking.bookingDate?.toDate();
  const isUpcoming = booking.status === 'confirmed' && bookingDate && new Date(bookingDate) > new Date();
  
  return (
     <Container maxWidth='md' sx={{ py: 4 }}>
        <Button
           startIcon={<ArrowBackIcon />}
           onClick={() => navigate('/my-bookings')}
           sx={{ mb: 3 }}
        >
           Back to My Bookings
        </Button>

        <Box
           sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 3,
           }}
        >
           <Box>
              <Typography variant='h4' component='h1' gutterBottom>
                 {booking.packageName}
              </Typography>
              <Typography variant='subtitle1' color='text.secondary'>
                 at {booking.venueName}
              </Typography>
           </Box>

           <Chip
              label={booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
              color={
                 booking.status === 'confirmed'
                    ? 'success'
                    : booking.status === 'completed'
                      ? 'primary'
                      : booking.status === 'cancelled'
                        ? 'error'
                        : 'default'
              }
              size='medium'
           />
        </Box>

        <Grid container spacing={3}>
           <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3, mb: 3 }}>
                 <Typography variant='h6' gutterBottom>
                    Booking Details
                 </Typography>

                 <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                       <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Box>
                             <Typography variant='body2' color='text.secondary'>
                                Date
                             </Typography>
                             <Typography variant='body1'>
                                {bookingDate ? bookingDate.toLocaleDateString() : 'N/A'}
                             </Typography>
                          </Box>
                       </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                       <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Box>
                             <Typography variant='body2' color='text.secondary'>
                                Time
                             </Typography>
                             <Typography variant='body1'>
                                {`${booking.timeSlot.start} - ${booking.timeSlot.end}`}
                             </Typography>
                          </Box>
                       </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                       <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <GroupIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Box>
                             <Typography variant='body2' color='text.secondary'>
                                Guests
                             </Typography>
                             <Typography variant='body1'>
                                {booking.guests} {booking.guests === 1 ? 'Guest' : 'Guests'}
                             </Typography>
                          </Box>
                       </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                       <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PlaceIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Box>
                             <Typography variant='body2' color='text.secondary'>
                                Venue
                             </Typography>
                             <Typography variant='body1'>{booking.venueName}</Typography>
                          </Box>
                       </Box>
                    </Grid>
                 </Grid>

                 {booking.specialRequests && (
                    <Box sx={{ mt: 2 }}>
                       <Typography variant='body2' color='text.secondary'>
                          Special Requests
                       </Typography>
                       <Typography variant='body1'>{booking.specialRequests}</Typography>
                    </Box>
                 )}

                 <Divider sx={{ my: 2 }} />

                 <Box>
                    <Typography variant='body2' color='text.secondary'>
                       Booking ID
                    </Typography>
                    <Typography variant='body1' fontFamily='monospace'>
                       {booking.id}
                    </Typography>
                 </Box>
              </Paper>

              <Paper sx={{ p: 3 }}>
                 <Typography variant='h6' gutterBottom>
                    Payment Details
                 </Typography>

                 <Grid container spacing={2}>
                    <Grid item xs={6}>
                       <Typography variant='body2' color='text.secondary'>
                          Amount Paid
                       </Typography>
                    </Grid>
                    <Grid item xs={6}>
                       <Typography variant='body1' align='right' fontWeight='medium'>
                          ${booking.amount.toFixed(2)}
                       </Typography>
                    </Grid>

                    <Grid item xs={6}>
                       <Typography variant='body2' color='text.secondary'>
                          Payment ID
                       </Typography>
                    </Grid>
                    <Grid item xs={6}>
                       <Typography variant='body1' align='right' fontFamily='monospace'>
                          {booking.paymentId || 'N/A'}
                       </Typography>
                    </Grid>

                    <Grid item xs={6}>
                       <Typography variant='body2' color='text.secondary'>
                          Payment Date
                       </Typography>
                    </Grid>
                    <Grid item xs={6}>
                       <Typography variant='body1' align='right'>
                          {booking.createdAt
                             ? booking.createdAt.toDate().toLocaleDateString()
                             : 'N/A'}
                       </Typography>
                    </Grid>
                 </Grid>
              </Paper>
           </Grid>

           <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
                 <Typography variant='h6' gutterBottom>
                    Redemption Code
                 </Typography>

                 <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant='h5' fontFamily='monospace' fontWeight='bold'>
                       {booking.redemptionCode}
                    </Typography>
                 </Box>

                 <Typography variant='body2' color='text.secondary' paragraph>
                    Show this code at the venue to redeem your package.
                 </Typography>

                 {/* <QRCode 
              value={`https://wetrippen.com/redeem/${booking.venueId}/${booking.redemptionCode}`}
              size={180}
            /> */}
              </Paper>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                 {isUpcoming && (
                    <Button
                       fullWidth
                       variant='outlined'
                       color='error'
                       startIcon={<CancelIcon />}
                       onClick={handleCancelBooking}
                    >
                       Cancel Booking
                    </Button>
                 )}

                 <Button
                    fullWidth
                    variant='outlined'
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                 >
                    Print Booking
                 </Button>

                 <Button
                    fullWidth
                    variant='outlined'
                    onClick={() =>
                       navigate(`/explore/packages/${booking.venueId}/${booking.packageId}`)
                    }
                 >
                    View Package
                 </Button>
              </Box>
           </Grid>
        </Grid>
     </Container>
  );
};

export default BookingDetailPage;
